import React, { useState } from "react";
import { motion } from "framer-motion";
import Icon from "../../../components/AppIcon";
import {
  fadeInUp,
  fadeInLeft,
  fadeInRight,
  staggerContainer,
  staggerItem,
  useInView,
} from "../../../utils/animations";

const ProblemSection = () => {
  const [selectedBusinessType, setSelectedBusinessType] = useState("retail");

  const businessTypes = [
    {
      id: "retail",
      label: "Retail Store",
      icon: "Store",
      problems: [
        "Manual inventory tracking leads to stockouts",
        "Lost sales due to pricing errors",
        "No visibility across multiple locations",
        "Hours spent on spreadsheet management",
      ],
    },
    {
      id: "restaurant",
      label: "Restaurant",
      icon: "UtensilsCrossed",
      problems: [
        "Food waste from poor inventory control",
        "Manual order tracking causes delays",
        "No real-time kitchen coordination",
        "Difficulty managing staff schedules",
      ],
    },
    {
      id: "multi-location",
      label: "Multi-Location",
      icon: "MapPin",
      problems: [
        "Cannot track performance across locations",
        "Inconsistent pricing and inventory",
        "Communication gaps between locations",
        "Consolidated reporting is impossible",
      ],
    },
  ];

  const selectedBusiness = businessTypes?.find(
    (type) => type?.id === selectedBusinessType
  );

  const chaosScenarios = [
    {
      title: "Lost Revenue",
      amount: "$4,500",
      description: "Monthly revenue lost due to stockouts and manual errors",
      icon: "TrendingDown",
      color: "text-error",
    },
    {
      title: "Wasted Time",
      amount: "20+ Hours",
      description: "Weekly hours spent on manual inventory and reporting",
      icon: "Clock",
      color: "text-warning",
    },
    {
      title: "Missed Opportunities",
      amount: "30%",
      description: "Potential growth missed due to lack of business insights",
      icon: "AlertTriangle",
      color: "text-error",
    },
  ];

  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  return (
    <motion.section
      ref={ref}
      id="problems"
      className="py-16 lg:py-24 bg-gray-50"
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      variants={staggerContainer}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div className="text-center mb-16" variants={staggerItem}>
          <motion.div
            className="inline-flex items-center px-4 py-2 bg-error/10 rounded-full text-error text-sm font-medium mb-6"
            variants={fadeInUp}
          >
            <Icon name="AlertTriangle" size={16} className="mr-2" />
            The Hidden Cost of Business Chaos
          </motion.div>
          <motion.h2
            className="text-3xl lg:text-5xl font-bold text-text-primary mb-6"
            variants={fadeInUp}
          >
            Are You Losing Money Every Day?
          </motion.h2>
          <motion.p
            className="text-lg text-text-secondary max-w-3xl mx-auto"
            variants={fadeInUp}
          >
            Small business owners lose an average of $15,000 annually due to
            poor inventory management, manual processes, and lack of real-time
            business insights.
          </motion.p>
        </motion.div>

        {/* Business Type Selector */}
        <motion.div
          className="flex flex-wrap justify-center gap-4 mb-12"
          variants={staggerItem}
        >
          {businessTypes?.map((type, index) => (
            <motion.button
              key={type?.id}
              onClick={() => setSelectedBusinessType(type?.id)}
              className={`flex items-center px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                selectedBusinessType === type?.id
                  ? "bg-primary text-white shadow-lg"
                  : "bg-white text-text-secondary hover:bg-gray-50 border border-gray-200"
              }`}
              variants={fadeInUp}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Icon
                name={type?.icon}
                size={20}
                className={`mr-2 ${
                  selectedBusinessType === type?.id
                    ? "text-white"
                    : "text-text-secondary"
                }`}
              />
              {type?.label}
            </motion.button>
          ))}
        </motion.div>

        {/* Split Screen Comparison */}
        <motion.div
          className="grid lg:grid-cols-2 gap-8 lg:gap-12 mb-16"
          variants={staggerItem}
        >
          {/* Chaos Side */}
          <div className="bg-white rounded-2xl p-8 shadow-lg border-l-4 border-error">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-error/10 rounded-lg flex items-center justify-center mr-4">
                <Icon name="AlertTriangle" size={24} className="text-error" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-text-primary">
                  Current Chaos
                </h3>
                <p className="text-text-secondary">
                  Manual processes & spreadsheets
                </p>
              </div>
            </div>

            {/* Chaos Visualization */}
            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="bg-white p-3 rounded border-l-2 border-error">
                  <div className="text-xs text-text-secondary">Inventory</div>
                  <div className="font-medium text-error">Out of Stock</div>
                </div>
                <div className="bg-white p-3 rounded border-l-2 border-warning">
                  <div className="text-xs text-text-secondary">Sales</div>
                  <div className="font-medium text-warning">Manual Entry</div>
                </div>
                <div className="bg-white p-3 rounded border-l-2 border-error">
                  <div className="text-xs text-text-secondary">Reports</div>
                  <div className="font-medium text-error">Outdated</div>
                </div>
              </div>
              <div className="text-center text-sm text-text-secondary">
                <Icon
                  name="Frown"
                  size={20}
                  className="mx-auto mb-2 text-error"
                />
                Frustrated business owner
              </div>
            </div>

            {/* Problems List */}
            <div className="space-y-3">
              {selectedBusiness?.problems?.map((problem, index) => (
                <div key={index} className="flex items-start">
                  <Icon
                    name="X"
                    size={16}
                    className="text-error mt-1 mr-3 flex-shrink-0"
                  />
                  <span className="text-text-secondary">{problem}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Solution Side */}
          <div className="bg-white rounded-2xl p-8 shadow-lg border-l-4 border-success">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center mr-4">
                <Icon name="CheckCircle" size={24} className="text-success" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-text-primary">
                  TAK Solution
                </h3>
                <p className="text-text-secondary">Automated & streamlined</p>
              </div>
            </div>

            {/* Solution Visualization */}
            <div className="bg-gradient-to-br from-success/5 to-primary/5 rounded-lg p-6 mb-6">
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="bg-white p-3 rounded border-l-2 border-success">
                  <div className="text-xs text-text-secondary">Inventory</div>
                  <div className="font-medium text-success">Real-time</div>
                </div>
                <div className="bg-white p-3 rounded border-l-2 border-success">
                  <div className="text-xs text-text-secondary">Sales</div>
                  <div className="font-medium text-success">Automated</div>
                </div>
                <div className="bg-white p-3 rounded border-l-2 border-success">
                  <div className="text-xs text-text-secondary">Reports</div>
                  <div className="font-medium text-success">Live Data</div>
                </div>
              </div>
              <div className="text-center text-sm text-text-secondary">
                <Icon
                  name="Smile"
                  size={20}
                  className="mx-auto mb-2 text-success"
                />
                Happy & profitable business owner
              </div>
            </div>

            {/* Benefits List */}
            <div className="space-y-3">
              <div className="flex items-start">
                <Icon
                  name="Check"
                  size={16}
                  className="text-success mt-1 mr-3 flex-shrink-0"
                />
                <span className="text-text-secondary">
                  Real-time inventory tracking prevents stockouts
                </span>
              </div>
              <div className="flex items-start">
                <Icon
                  name="Check"
                  size={16}
                  className="text-success mt-1 mr-3 flex-shrink-0"
                />
                <span className="text-text-secondary">
                  Automated sales processing eliminates errors
                </span>
              </div>
              <div className="flex items-start">
                <Icon
                  name="Check"
                  size={16}
                  className="text-success mt-1 mr-3 flex-shrink-0"
                />
                <span className="text-text-secondary">
                  Multi-location visibility in one dashboard
                </span>
              </div>
              <div className="flex items-start">
                <Icon
                  name="Check"
                  size={16}
                  className="text-success mt-1 mr-3 flex-shrink-0"
                />
                <span className="text-text-secondary">
                  Instant reports save hours of manual work
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Chaos Impact Metrics */}
        <motion.div
          className="grid md:grid-cols-3 gap-6"
          variants={staggerItem}
        >
          {chaosScenarios?.map((scenario, index) => (
            <motion.div
              key={index}
              className="bg-white rounded-xl p-6 shadow-lg text-center backdrop-blur-sm border border-gray-100"
              variants={fadeInUp}
              whileHover={{
                scale: 1.05,
                boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
              }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            >
              <div
                className={`w-16 h-16 ${scenario?.color?.replace(
                  "text-",
                  "bg-"
                )}/10 rounded-full flex items-center justify-center mx-auto mb-4`}
              >
                <Icon
                  name={scenario?.icon}
                  size={24}
                  className={scenario?.color}
                />
              </div>
              <div className={`text-2xl font-bold ${scenario?.color} mb-2`}>
                {scenario?.amount}
              </div>
              <h4 className="font-semibold text-text-primary mb-2">
                {scenario?.title}
              </h4>
              <p className="text-sm text-text-secondary">
                {scenario?.description}
              </p>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA */}
        <motion.div className="text-center mt-12" variants={staggerItem}>
          <motion.p
            className="text-lg text-text-secondary mb-6"
            variants={fadeInUp}
          >
            Stop losing money to business chaos. Transform your operations
            today.
          </motion.p>
          <motion.button
            onClick={() =>
              document
                .getElementById("solution-section")
                ?.scrollIntoView({ behavior: "smooth" })
            }
            className="inline-flex items-center px-8 py-4 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 transition-colors shadow-cta backdrop-blur-sm"
            variants={fadeInUp}
            whileHover={{
              scale: 1.05,
              boxShadow: "0 20px 40px -12px rgba(59, 130, 246, 0.5)",
            }}
            whileTap={{ scale: 0.95 }}
          >
            See The Solution
            <Icon name="ArrowRight" size={20} className="ml-2" />
          </motion.button>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default ProblemSection;
